import React, {
    useEffect,
    useState,
} from "react";
import {
    Box,
    Drawer,
    Pagination,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useSearchParams} from "react-router-dom";
import Filters from "./Filters";
import {useDataFetching} from "../../hooks/useDataFetching";
import {CatalogueContent} from "./list/CatalogueContent";
import {getOffset} from "../../utils/getOffset";
import {ButtonTableView} from "../buttons/ButtonTableView";
import {ViewTableStyle} from "../../enums/ViewTableStyle";
import {ButtonsToggleFilters} from "../buttons/ButtonsToggleFilters";
import {GetSelectedFiltersText} from "./list/GetSelectedFiltersText";
import {useFavourites} from "../favourites/FavouritesContext";

const rowsPerPage = 10;

export const Catalogue: React.FC = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const [searchParams, setSearchParams] = useSearchParams();
    const [minMaxCC, setMinMaxCC] = useState<number[]>([50, 2000]);
    const [minMaxYear, setMinMaxYear] = useState<number[]>([1900, 2025]);
    const [page, setPage] = useState<number>(1);
    const [viewStyle, setViewStyle] = useState<ViewTableStyle>(ViewTableStyle.LIST);
    const {favouritesIds, toggleFavourite} = useFavourites();

    const {
        makers,
        models,
        types,
        motorcycles,
        selectedMaker,
        selectedModel,
        selectedType,
        dataLoading,
        resetFilters,
        loadMakersAndTypes,
        loadMotorcycles,
        loadModels,
        setModels,
        setSelectedMaker,
        setSelectedModel,
        setSelectedType,
    } = useDataFetching();

    useEffect(() => {
        const savedViewStyle = localStorage.getItem("viewStyle");
        if (savedViewStyle) {
            setViewStyle(JSON.parse(savedViewStyle));
        }
        const pageFromQuery = parseInt(searchParams.get("page") || "1", 10);
        setPage(pageFromQuery);
        handleFilter();
    }, [searchParams]);

    useEffect(() => {
        loadMakersAndTypes();
        loadMotorcycles({
            makerId: selectedMaker,
            modelId: selectedModel,
            typeId: selectedType,
            minCC: minMaxCC[0],
            maxCC: minMaxCC[1],
            minYear: minMaxYear[0],
            maxYear: minMaxYear[1],
            offset: getOffset(page, rowsPerPage),
        });
    }, []);

    useEffect(() => {
        if (selectedMaker) {
            loadModels(selectedMaker);
        }
    }, [selectedMaker]);

    const handlerReset = () => {
        resetFilters();
        setMinMaxCC([50, 2000]);
        setMinMaxYear([1900, 2025]);
        setPage(1);
        setSearchParams({page: "1"});
        setModels([]);
        loadMotorcycles({
            makerId: null,
            modelId: null,
            typeId: null,
            minCC: 50,
            maxCC: 2000,
            minYear: 1900,
            maxYear: 2025,
            offset: getOffset(1, rowsPerPage),
        });
    };

    const handleFilter = () => {
        loadMotorcycles({
            makerId: selectedMaker,
            modelId: selectedModel,
            typeId: selectedType,
            minCC: minMaxCC[0],
            maxCC: minMaxCC[1],
            minYear: minMaxYear[0],
            maxYear: minMaxYear[1],
            offset: getOffset(page, rowsPerPage),
        });

        setFiltersOpen(false);
    };

    const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        setSearchParams({page: value.toString()});
    };

    const [filtersOpen, setFiltersOpen] = useState(false);
    const handleFiltersToggle = () => {
        setFiltersOpen(!filtersOpen);
    };

    const mobileFilters =
        <>
            <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                <ButtonsToggleFilters onclick={handleFiltersToggle}/>
                <Typography variant="body2" sx={{flexGrow: 1}}>
                    <GetSelectedFiltersText
                        selectedMaker={selectedMaker}
                        selectedModel={selectedModel}
                        selectedType={selectedType}
                        minMaxCC={minMaxCC}
                        minMaxYear={minMaxYear}
                        makers={makers}
                        models={models}
                        types={types}
                    />
                </Typography>
            </Box>
            <Drawer
                anchor="left"
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                sx={{
                    display: {xs: "block", sm: "block", md: "none"},
                }}
            >
                <Filters
                    makers={makers}
                    models={models}
                    types={types}
                    selectedMaker={selectedMaker}
                    selectedModel={selectedModel}
                    selectedType={selectedType}
                    setSelectedMaker={setSelectedMaker}
                    setSelectedModel={setSelectedModel}
                    setSelectedType={setSelectedType}
                    resetFilters={handlerReset}
                    handleFilter={handleFilter}
                    minMaxCC={minMaxCC}
                    setMinMaxCC={setMinMaxCC}
                    minMaxYear={minMaxYear}
                    setMinMaxYear={setMinMaxYear}
                />
            </Drawer>
        </>;

    return (
        <Box
            sx={{
                borderRadius: 2,
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <Box sx={{display: "flex", gap: 2, flexDirection: {xs: "column", md: "row"}}}>
                {!isSmallScreen && (
                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: (theme) => theme.palette.card.background,
                            borderRadius: 2,
                            p: 2,
                        }}
                    >
                        <Filters
                            makers={makers}
                            models={models}
                            types={types}
                            selectedMaker={selectedMaker}
                            selectedModel={selectedModel}
                            selectedType={selectedType}
                            setSelectedMaker={setSelectedMaker}
                            setSelectedModel={setSelectedModel}
                            setSelectedType={setSelectedType}
                            resetFilters={handlerReset}
                            handleFilter={handleFilter}
                            minMaxCC={minMaxCC}
                            setMinMaxCC={setMinMaxCC}
                            minMaxYear={minMaxYear}
                            setMinMaxYear={setMinMaxYear}
                        />
                    </Box>
                )}
                <Box sx={{flex: 3, borderRadius: 2}}>
                    <ButtonTableView viewStyle={viewStyle}
                        setViewStyle={setViewStyle}
                        filters={isSmallScreen ? mobileFilters : undefined}
                        isSmallScreen={isSmallScreen}
                    />
                    <CatalogueContent
                        motorcycles={motorcycles?.data ?? null}
                        dataLoading={dataLoading}
                        viewStyle={viewStyle}
                        favourites={favouritesIds}
                        toggleFavourites={toggleFavourite}
                    />
                </Box>
            </Box>
            <Box sx={{display: "flex", justifyContent: "center", pt: 2}}>
                <Pagination
                    count={Math.ceil(motorcycles ? motorcycles.totalRecords / rowsPerPage : 0)}
                    page={page}
                    onChange={handleChangePage}
                    variant="outlined"
                    shape="rounded"
                />
            </Box>
        </Box>
    );
};
